import React, { useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  Alert,
  BlankState,
  Box,
  Button,
  ContentTemplate,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  Progress,
  SectionHeader,
  TableActionTypes,
  TableState,
  TitleHeader,
} from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useMediaQuery } from '@mui/material';
import {
  TABLE_INITIAL_STATE,
  useDataTable,
} from 'web-library/stories/organism/DataTable/DataTable';
import RouteKey from '../../routes/key';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../utils/DateUtils';
import {
  CoordinatorAppealListingResponse,
  useGetCoordinatorAppealListing,
} from '../../api-refactored/appeal/useGetCoordinatorAppealListing';
import { useWOGAA } from '../../hooks/wogaa';

const { appeal } = RouteKey.child;
const { importantNotes, summary, applicationsCoordinator } = RouteKey.appeal.child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  emptyReserve: {
    height: 314,
  },
  emptyContainer: {
    width: '100%',
    position: 'absolute',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 77,
    paddingBottom: 77,
  },
  viewDetailsButton: {
    display: 'flex',
    alignSelf: 'flex-start',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  appealStartButton: {
    height: 'fit-content',
    marginBottom: theme.spacing(3),
    '@supports (-webkit-touch-callout: none)': {
      '@media (hover: none)': {
        '&:hover': {
          backgroundColor: `${theme.color.link.linkDefault02} !important`,
          boxShadow: 'none',
        },
      },
    },
  },
  pillContainer: {},
  feeColumnHeader: {
    textAlign: 'right',
  },
  bottomMargin: {
    marginBottom: theme.spacing(7),
  },
}));

enum StatusOptions {
  pendingPayment = 'PENDING_PAYMENT',
  submitted = 'SUBMITTED',
  registered = 'REGISTERED',
  draft = 'DRAFT',
}

interface ViewAppealPageProps {
  title: string;
}

const getBlankStateDisplayButton = (
  data: CoordinatorAppealListingResponse,
  t: TFunction<'translation'>,
  handleBackToHome: () => void,
  handleStartAppeal: () => void
) => {
  if (data.appealOpen) {
    return (
      <Button
        disabled={!data.resultReleaseOpen}
        id="button-startNewAppealDatatable"
        size="small"
        color="primary"
        variant="text"
        onClick={handleStartAppeal}
      >
        {t('appeal->>AppealCoordinatorListing.NewApplication')}
      </Button>
    );
  }
  return (
    <Button
      id="button-backToHomepageDatatable"
      size="small"
      color="primary"
      variant="text"
      onClick={handleBackToHome}
    >
      {t('appeal->>AppealListing.BackToHomepage')}
    </Button>
  );
};

const handleViewDetails = (
  state: TableState,
  uuid: string,
  history: RouteComponentProps['history']
) => {
  history.push(`/${appeal}/${applicationsCoordinator.route}/${uuid}/${summary.route}`, {
    pageSize: state.size,
    page: state.page,
  });
};

const tableColumns = (
  state: TableState,
  t: TFunction<'translation'>,
  classes: Record<string,string>,
  history: RouteComponentProps['history']
): MUIDataTableColumnDef[] => [
    {
      name: 'nricFin',
      label: 'NRIC/FIN/Passport No.',
      options: { setCellProps: () => ({ style: { width: '10%' } }) },
    },
    {
      name: 'candidateName',
      label: 'Candidate Name',
      options: { setCellProps: () => ({ style: { width: '13%' } }) },
    },
    {
      name: 'examLevel',
      label: 'Exam Level(s)',
      options: { setCellProps: () => ({ style: { width: '12%' } }) },
    },
    {
      name: 'totalPayableFee',
      label: 'Total Appeal Fees (SGD)',
      options: {
        setCellProps: () => ({ style: { width: '8%' } }),
        customHeadLabelRender: () => (
          <Grid container justify="flex-end" className={classes.feeColumnHeader}>
            {t('appeal->>AppealCoordinatorListing.TotalAppealFees')}
          </Grid>
        ),
        customBodyRenderLite: (dataIndex: number) => (
          <Grid container justify="flex-end">
            {state.data[dataIndex].totalPayableFee === null
              ? 'N/A'
              : `$${state.data[dataIndex].totalPayableFee}`}
          </Grid>
        ),
      },
    },
    {
      name: 'coordinatorName',
      label: 'Coordinator',
      options: { setCellProps: () => ({ style: { width: '12%' } }) },
    },
    {
      name: 'applicationDateTime',
      label: 'Application Date/Time',
      options: {
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRenderLite: (dataIndex: number) =>
          stringToLongDateAndShortTime(state.data[dataIndex].dateOfApplication),
      },
    },
    {
      name: 'applicationStatus',
      label: 'Status',
      options: {
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRender: (value: any) => {
          switch (true) {
            case value === StatusOptions.submitted:
            case value === StatusOptions.pendingPayment:
              return (
                <Pill
                  className={classes.pillContainer}
                  label={t('appeal->>AppealCoordinatorListing.PendingPayment')}
                  variant="info"
                />
              );
            case value === StatusOptions.registered:
              return (
                <Pill
                  className={classes.pillContainer}
                  label={t('appeal->>View.Submitted')}
                  variant="positive"
                />
              );
            default:
              return null;
          }
        },
      },
    },
    {
      name: 'appealOutcome',
      label: 'Appeal Outcome',
      options: {
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRenderLite: (dataIndex: number) =>
          state.data[dataIndex].appealOutcomeDateTime
            ? `Last updated on ${stringToSemanticDisplay(
              state.data[dataIndex].appealOutcomeDateTime
            )}`
            : '-',
      },
    },
    {
      name: 'action',
      label: 'Action(s)',
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const { applicationStatus } = state.data[dataIndex];
          return (
            <>
              {(applicationStatus === StatusOptions.submitted ||
                applicationStatus === StatusOptions.registered ||
                applicationStatus === StatusOptions.pendingPayment) && (
                  <Button
                    color="primary"
                    size="small"
                    variant="text"
                    className={classes.viewDetailsButton}
                    onClick={() => handleViewDetails(state, state.data[dataIndex].uuid, history)}
                  >
                    {t('appeal->>AppealCoordinatorListing.ViewApplication')}
                  </Button>
                )}
            </>
          );
        },
      },
    },
  ];

const generateTableOptions = (
  data: CoordinatorAppealListingResponse,
  t: TFunction<'translation'>,
  classes: Record<string,string>,
  handleBackToHome: () => void,
  handleStartAppeal: () => void
) => ({
  textLabels: {
    body: {
      noMatch: (
        <>
          <Box className={classes.emptyReserve}>
            <></>
          </Box>
          <Box className={classes.emptyContainer}>
            <BlankState
              label={
                data.appealOpen
                  ? t('appeal->>AppealListing.NoPendingAppeal')
                  : t('appeal->>AppealListing.AppealUnavailable')
              }
            />
            {getBlankStateDisplayButton(data, t, handleBackToHome, handleStartAppeal)}
          </Box>
        </>
      ),
    },
  },
  pagination: true,
});

const CoordinatorAppealListing = ({ title }: ViewAppealPageProps) => {
  const { t } = useTranslation();
  const history = useHistory<{ page: number; pageSize: number }>();

  const header = t('appeal->>AppealCoordinatorListing.header');
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { state, dispatch: tableDispatch } = useDataTable({
    ...TABLE_INITIAL_STATE,
    page: history?.location?.state?.page || 0,
    size: history?.location?.state?.pageSize || 5,
    sortColumn: 'applicationDateTime',
    order: 'desc',
  });

  const {
    data: appealApplicationListing,
    isLoading: isLoadingAppealApplicationListing,
    isFetching: isFetchingAppealApplicationListing,
    refetch: refetchAppealApplicationListing,
  } = useGetCoordinatorAppealListing({
    apiParams: {
      page: state.page,
      pageSize: state.size,
    },
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.resultPage.content,
            count: data.resultPage.totalElements,
          },
        } as TableActionTypes);
      }
    },
  });

  const refreshAACandidateListing = () => {
    refetchAppealApplicationListing();
  };

  useEffect(refreshAACandidateListing, [refetchAppealApplicationListing, state.page, state.size]);

  const { transactionStart } = useWOGAA('myexamsseab-6474');

  const handleStartAppeal = () => {
    transactionStart();
    history.push(`/${appeal}/${applicationsCoordinator.route}/${importantNotes.route}`);
  };
  const handleBackToHome = () => {
    history.push(`/`);
  };

  return (<>
    <TitleHeader title={title} />
    {isLoadingAppealApplicationListing ? (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    ) : (
      <ContentTemplate type="full" continuous transparent>
        {appealApplicationListing && (
          <>
            <Grid className={classes.headerContainer} container justify="space-between">
              <SectionHeader title={header} />
              <Grid xs={12} md={4} container justify="flex-end">
                <Button
                  id="button-View.Appeal-start"
                  fullWidth={isMobile}
                  onClick={handleStartAppeal}
                  disabled={
                    !appealApplicationListing.resultReleaseOpen ||
                    !appealApplicationListing.appealOpen
                  }
                  className={classes.appealStartButton}
                >
                  {t('appeal->>AppealCoordinatorListing.NewApplication')}
                </Button>
              </Grid>
            </Grid>
            {!appealApplicationListing.resultReleaseOpen && (
              <Alert
                className={classes.alert}
                variant="filled"
                severity="error"
                message={t('appeal->>AppealListing.ResultUnavailable')}
              />
            )}
            <Grid className={classes.bottomMargin}>
              <MUIDataTable
                columns={tableColumns(state, t, classes, history)}
                dispatch={tableDispatch}
                state={state}
                data={state.data}
                isLoading={isFetchingAppealApplicationListing}
                options={generateTableOptions(
                  appealApplicationListing,
                  t,
                  classes,
                  handleBackToHome,
                  handleStartAppeal
                )}
                rowsPerPageOptions={[5, 10, 20, 30, 50]}
              />
            </Grid>
          </>
        )}
      </ContentTemplate>
    )}
  </>);
};

export default CoordinatorAppealListing;
